import { Card } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { MapPin, Building2, Calendar, Play } from 'lucide-react';
import { ImpactRecord, IMPACT_CATEGORY_BADGE, getYoutubeId } from '@/hooks/useImpactRecords';

const FALLBACK_IMG = 'https://images.unsplash.com/photo-1551434678-e076c223a692?w=800&h=600&fit=crop';

function stripHtml(html: string | null | undefined): string {
  if (!html) return '';
  return html.replace(/<[^>]*>/g, ' ').replace(/\s+/g, ' ').trim();
}

interface Props {
  record: ImpactRecord;
  onClick?: (r: ImpactRecord) => void;
}

export function ImpactCard({ record, onClick }: Props) {
  const ytId = getYoutubeId(record.video_url);
  const thumb =
    record.image_url ||
    (ytId ? `https://img.youtube.com/vi/${ytId}/hqdefault.jpg` : FALLBACK_IMG);

  return (
    <Card
      onClick={() => onClick?.(record)}
      className="group overflow-hidden cursor-pointer border-border/60 hover:border-primary/40 hover:shadow-2xl transition-all duration-300 hover:-translate-y-1 bg-card"
    >
      <div className="relative aspect-[4/3] overflow-hidden bg-muted">
        <img
          src={thumb}
          alt={record.title}
          loading="lazy"
          className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-110"
        />
        <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/10 to-transparent" />
        <Badge className="absolute top-3 left-3 bg-primary/90 text-primary-foreground border-0 backdrop-blur-sm">
          {IMPACT_CATEGORY_BADGE[record.category]}
        </Badge>
        {ytId && (
          <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
            <div className="w-14 h-14 rounded-full bg-black/60 backdrop-blur-sm flex items-center justify-center group-hover:scale-110 transition-transform">
              <Play className="h-6 w-6 text-white fill-white ml-1" />
            </div>
          </div>
        )}
        <div className="absolute bottom-0 left-0 right-0 p-4 text-white">
          <h3 className="text-lg font-bold leading-tight line-clamp-2" title={record.title}>{record.title}</h3>
          {record.subtitle && (
            <p className="text-sm text-white/85 mt-1 line-clamp-1" title={record.subtitle}>{record.subtitle}</p>
          )}
        </div>
      </div>
      <div className="p-4 space-y-2">
        {record.description && (
          <p className="text-sm text-muted-foreground line-clamp-2">
            {stripHtml(record.description)}
          </p>
        )}
        <div className="flex flex-wrap gap-3 text-xs text-muted-foreground pt-1">
          {record.company && (
            <span className="inline-flex items-center gap-1">
              <Building2 className="h-3 w-3" /> {record.company}
            </span>
          )}
          {record.location && (
            <span className="inline-flex items-center gap-1">
              <MapPin className="h-3 w-3" /> {record.location}
            </span>
          )}
          {record.date && (
            <span className="inline-flex items-center gap-1">
              <Calendar className="h-3 w-3" />
              {new Date(record.date).toLocaleDateString('es-ES', { year: 'numeric', month: 'short' })}
            </span>
          )}
        </div>
      </div>
    </Card>
  );
}
